import { ReactElement, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';

import { LIQUID_STAKING_TOKEN_ADDRESS } from 'modules/api/const';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { BorderedPaper } from 'modules/common/components/BorderedPaper';
import { ClaimTabStatus } from 'modules/delegateStaking/components/ClaimTabStatus';
import { DeprecatedLiquidStakingRoutesConfig } from 'modules/deprecatedLiquidStaking/Routes';
import { useTranslation } from 'modules/i18n';
import { useGetLSPendingUnstakesQuery } from 'modules/liquidStaking/actions/getPendingUnstakes';
import { Unstake } from 'modules/liquidStaking/components/Unstake';
import { Withdrawal } from 'modules/liquidStaking/components/Withdrawal';
import { LiquidStakingTab } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { LiquidStakingRoutesConfig } from 'modules/liquidStaking/Routes';

import { Stake } from '../Stake';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function Forms(): ReactElement {
  const { classes, cx } = useStyles();
  const { poolAddress, isDeprecated } = useLiquidStakingPool();

  const RoutesConfig = isDeprecated
    ? DeprecatedLiquidStakingRoutesConfig
    : LiquidStakingRoutesConfig;
  const { tab } = RoutesConfig.dashboard.useParams();

  const navigate = useNavigate();
  const { t, keys } = useTranslation(translation);

  const { isConnected, connectionKey } = useConnection();

  const { data: poolUnstakes } = useGetLSPendingUnstakesQuery(
    { connectionKey, poolAddress },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  useEffect(() => {
    if (tab && !Object.values(LiquidStakingTab).includes(tab)) {
      navigate(RoutesConfig.dashboard.generatePath());
    } else if (isDeprecated && tab === LiquidStakingTab.Stake) {
      navigate(
        DeprecatedLiquidStakingRoutesConfig.dashboard.generatePath(
          LiquidStakingTab.Unstake,
        ),
      );
    }
  }, [RoutesConfig.dashboard, isDeprecated, navigate, tab]);

  return (
    <BorderedPaper>
      <div className={classes.content}>
        <div className={classes.tabs}>
          {!isDeprecated && (
            <Typography
              className={cx(
                classes.tab,
                tab === LiquidStakingTab.Stake && classes.activeTab,
              )}
              component={Link}
              to={RoutesConfig.dashboard.generatePath(LiquidStakingTab.Stake)}
            >
              {t(keys.stake)}
            </Typography>
          )}

          <Typography
            className={cx(
              classes.tab,
              tab === LiquidStakingTab.Unstake && classes.activeTab,
            )}
            component={Link}
            to={RoutesConfig.dashboard.generatePath(LiquidStakingTab.Unstake)}
          >
            {t(keys.unstake)}
          </Typography>

          <Typography
            className={cx(
              classes.tab,
              tab === LiquidStakingTab.Withdrawal && classes.activeTab,
            )}
            component={Link}
            to={RoutesConfig.dashboard.generatePath(
              LiquidStakingTab.Withdrawal,
            )}
          >
            <span>{t(keys.withdrawal)}</span>

            {!!poolUnstakes?.length && (
              <ClaimTabStatus
                poolAddress={LIQUID_STAKING_TOKEN_ADDRESS}
                poolUnstakes={poolUnstakes}
              />
            )}
          </Typography>
        </div>

        {tab === LiquidStakingTab.Stake && <Stake />}

        {tab === LiquidStakingTab.Unstake && <Unstake />}

        {tab === LiquidStakingTab.Withdrawal && <Withdrawal />}
      </div>
    </BorderedPaper>
  );
}
